import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { authFetch } from '@/lib/auth';
import { PhotoPicker, HighlightsInput } from '@/components/listing/EditorParts';
import { Loader2, ExternalLink, ArrowLeft } from 'lucide-react';
import { CopyShareLink, TextAgentButton, ShareCardPreview, shareUrlFor } from '@/components/listing/SharePanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'w-full h-11 rounded-lg bg-[#0f1724] border border-white/15 px-3 text-white placeholder:text-white/30 focus:border-[#C6A14A] outline-none';
const NUM_FIELDS = [['beds', 'Beds', '3'], ['baths', 'Baths', '2.5'], ['square_feet', 'Sq ft', '1850'], ['lot_square_feet', 'Lot (sq ft)', '9147'], ['year_built', 'Year built', '1989']];
const AGENT_FIELDS = [['name', 'Agent name'], ['brokerage', 'Brokerage'], ['phone', 'Phone'], ['email', 'Email']];

const toForm = (l) => ({
  ...Object.fromEntries(NUM_FIELDS.map(([k]) => [k, l[k] ?? ''])), price: l.price ?? '', show_price: !!l.show_price,
  highlights: l.highlights || [], agent: { name: '', brokerage: '', phone: '', email: '', ...(l.agent || {}) },
  hero_photo_id: l.hero_photo_id, excluded_photo_ids: l.excluded_photo_ids || [],
});
const numOrNull = (v) => (v === '' || v === null ? null : Number(v));

const Section = ({ title, children }) => (
  <section className="rounded-2xl border border-white/10 bg-[#0a1120] p-5 sm:p-6 space-y-4">
    <h2 className="font-serif text-xl text-white">{title}</h2>{children}
  </section>
);

const facts = (l) => [l.beds && `${l.beds} bed`, l.baths && `${l.baths} bath`, l.square_feet && `${Number(l.square_feet).toLocaleString()} sq ft`].filter(Boolean).join(' · ');

export default function ListingEditor() {
  const { gallerySlug } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    authFetch(`${API_BASE}/api/listings/manage/${gallerySlug}`).then(async (res) => {
      if (!active) return;
      if (!res.ok) { setError('You don’t have access to this gallery.'); return; }
      const d = await res.json();
      if (!active) return;
      setInfo(d);
      setForm((f) => f || (d.listing ? toForm(d.listing) : null));
    });
    return () => { active = false; };
  }, [gallerySlug]);

  const request = async (method, body) => {
    setBusy(true);
    setStatus(null);
    const res = await authFetch(`${API_BASE}/api/listings/manage/${gallerySlug}`, {
      method, headers: { 'Content-Type': 'application/json' }, body: body && JSON.stringify(body),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) {
      setStatus({ ok: false, text: typeof d.detail === 'string' ? d.detail : (d.detail?.[0]?.msg || 'Please check the values and try again.') });
      return null;
    }
    return d;
  };

  const create = async () => { const l = await request('POST'); if (l) { setInfo((i) => ({ ...i, listing: l })); setForm(toForm(l)); setStatus({ ok: true, text: 'Listing page created' }); } };
  const save = async () => {
    const body = { ...form, ...Object.fromEntries(NUM_FIELDS.map(([k]) => [k, numOrNull(form[k])])), price: numOrNull(form.price) };
    const l = await request('PUT', body);
    if (l) { setInfo((i) => ({ ...i, listing: l })); setStatus({ ok: true, text: 'Listing saved' }); }
  };
  const set = (k, v) => { setStatus(null); setForm((f) => ({ ...f, [k]: v })); };

  if (error) return <div className="min-h-screen bg-[#050816] pt-32 text-center text-[#E8E4DE]" data-testid="editor-error">{error}</div>;
  if (!info) return <div className="min-h-screen bg-[#050816] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C6A14A]" /></div>;
  const a = info.address;
  const listing = info.listing;

  return (
    <div className="min-h-screen bg-[#050816] pt-24 pb-40" data-testid="listing-editor">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-6">
        <Link to={`/property/${gallerySlug}`} className="inline-flex items-center gap-2 text-sm text-[#E8E4DE]/60 hover:text-[#C6A14A] min-h-[44px]"><ArrowLeft className="h-4 w-4" />Back to gallery</Link>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C6A14A]">Listing page</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-white mt-2">{a.street || gallerySlug}</h1>
            <p className="text-[#E8E4DE]/60">{[a.city, a.state].filter(Boolean).join(', ')}</p>
          </div>
          {listing && (
            <div className="flex flex-wrap gap-2">
              <a href={`/listing/${listing.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-[#C6A14A]/50 text-[#C6A14A] hover:bg-[#C6A14A]/10 text-sm" data-testid="editor-view-listing">
                View listing <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>

        {!listing ? (
          <Section title="Create a listing page">
            <p className="text-[#E8E4DE]/70">Turn this gallery into a single-property website with the photos, key details, a map and the agent's contact info.</p>
            <button type="button" onClick={create} disabled={busy} className="h-12 px-6 rounded-lg bg-[#C6A14A] text-[#050816] font-medium hover:bg-[#D4AF5A] inline-flex items-center gap-2" data-testid="editor-create-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}Create listing page
            </button>
          </Section>
        ) : form && (
          <>
            <Section title="Share">
              <p className="text-sm text-[#E8E4DE]/60">This is the card agents see when you text or post the link. Save after changing the hero photo to update it.</p>
              <ShareCardPreview listing={listing} address={a} facts={facts(listing)} />
              <div className="flex flex-wrap gap-2">
                <CopyShareLink url={shareUrlFor(listing.slug)} />
                <TextAgentButton phone={listing.agent?.phone} street={a.street} url={shareUrlFor(listing.slug)} />
              </div>
              {!listing.agent?.phone && <p className="text-xs text-[#E8E4DE]/40" data-testid="editor-no-agent-phone">No agent phone saved yet, so you'll pick the contact in Messages.</p>}
            </Section>
            <Section title="Hero & photos">
              <p className="text-sm text-[#E8E4DE]/60">Tap a photo to make it the hero. Use the eye button to hide a photo from the listing.</p>
              <PhotoPicker photos={info.photos} heroId={form.hero_photo_id} excluded={form.excluded_photo_ids}
                onHero={(id) => setForm((f) => ({ ...f, hero_photo_id: id, excluded_photo_ids: f.excluded_photo_ids.filter((x) => x !== id) }))}
                onToggleHidden={(id) => set('excluded_photo_ids', form.excluded_photo_ids.includes(id) ? form.excluded_photo_ids.filter((x) => x !== id) : [...form.excluded_photo_ids, id])} />
              <p className="text-xs text-[#E8E4DE]/40" data-testid="editor-photo-summary">{info.photos.length - form.excluded_photo_ids.length} of {info.photos.length} photos shown</p>
            </Section>

            <Section title="Key details">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {NUM_FIELDS.map(([k, label, ph]) => (
                  <label key={k} className="space-y-1.5 text-sm text-[#E8E4DE]/80">{label}
                    <input inputMode="decimal" value={form[k]} placeholder={ph} onChange={(e) => set(k, e.target.value.replace(/[^0-9.]/g, ''))} className={inputCls} data-testid={`editor-${k}`} />
                  </label>
                ))}
              </div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                <label className="space-y-1.5 text-sm text-[#E8E4DE]/80">List price ($)
                  <input inputMode="numeric" value={form.price} placeholder="e.g. 425000" onChange={(e) => set('price', e.target.value.replace(/\D/g, ''))} className={inputCls} data-testid="editor-price" />
                </label>
                <label className="flex items-center gap-3 h-11 text-sm text-[#E8E4DE] cursor-pointer">
                  <input type="checkbox" checked={form.show_price} onChange={(e) => set('show_price', e.target.checked)} className="h-5 w-5 accent-[#C6A14A]" data-testid="editor-show-price" />
                  Show price on listing
                </label>
              </div>
            </Section>

            <Section title="Highlights"><HighlightsInput items={form.highlights} onChange={(v) => set('highlights', v)} /></Section>

            <Section title="Agent">
              <div className="grid sm:grid-cols-2 gap-3">
                {AGENT_FIELDS.map(([k, label]) => (
                  <label key={k} className="space-y-1.5 text-sm text-[#E8E4DE]/80">{label}
                    <input value={form.agent[k]} placeholder={info.agent_placeholder[k]} type={k === 'email' ? 'email' : 'text'}
                      onChange={(e) => set('agent', { ...form.agent, [k]: e.target.value })} className={inputCls} data-testid={`editor-agent-${k}`} />
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#E8E4DE]/40">Empty fields show the placeholder on the listing page.</p>
            </Section>
          </>
        )}
      </div>
      {listing && form && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#050816]/95 backdrop-blur px-4 py-3">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-4">
            {status && <p role="status" className={`text-sm ${status.ok ? 'text-emerald-400' : 'text-red-400'}`} data-testid="editor-status">{status.text}</p>}
            <button type="button" onClick={save} disabled={busy} className="h-12 px-8 rounded-lg bg-[#C6A14A] text-[#050816] font-medium hover:bg-[#D4AF5A] inline-flex items-center gap-2 w-full sm:w-auto justify-center" data-testid="editor-save-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}Save listing
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
